import { useEffect, useCallback, useRef } from 'react';

export interface TelegramLifecycleOptions {
  autoReady?: boolean;
  autoExpand?: boolean;
  requestFullscreen?: boolean;
  disableVerticalSwipes?: boolean;
  enableClosingConfirmation?: boolean;
  onReady?: () => void;
  onClose?: () => void;
}

export interface TelegramLifecycleControls {
  ready: () => void;
  expand: () => void;
  requestFullscreen: () => void;
  close: () => void;
  isReady: boolean;
  isAvailable: boolean;
  isFullscreen: boolean;
}

export function useTelegramLifecycle(
  options: TelegramLifecycleOptions = {}
): TelegramLifecycleControls {
  const {
    autoReady = true,
    autoExpand = true,
    requestFullscreen: autoRequestFullscreen = true,
    disableVerticalSwipes: autoDisableVerticalSwipes = true,
    enableClosingConfirmation = false,
    onReady,
    onClose
  } = options;

  const readyCalledRef = useRef(false);
  const isFullscreenRef = useRef(false);
  const webapp = window.Telegram?.WebApp;
  const isAvailable = !!webapp;

  const ready = useCallback(() => {
    if (!webapp || readyCalledRef.current) return;

    console.log('🚀 Calling Telegram.WebApp.ready()');
    webapp.ready();
    readyCalledRef.current = true;
    onReady?.();
  }, [webapp, onReady]);

  const expand = useCallback(() => {
    if (!webapp) return;

    console.log('📏 Calling Telegram.WebApp.expand()');
    webapp.expand();
  }, [webapp]);

  const requestFullscreen = useCallback(() => {
    if (!webapp) return;

    // Request fullscreen mode (Telegram v8.0+)
    // Use type assertion for newer API not in SDK types yet
    const webappAny = webapp as any;
    if (typeof webappAny.requestFullscreen === 'function') {
      console.log('🖥️ Requesting fullscreen mode');
      webappAny.requestFullscreen();
      isFullscreenRef.current = true;
    } else {
      console.warn('⚠️ requestFullscreen not available in this Telegram version');
    }
  }, [webapp]);

  const close = useCallback(() => {
    if (!webapp) return;

    console.log('❌ Calling Telegram.WebApp.close()');
    onClose?.();
    webapp.close();
  }, [webapp, onClose]);

  // Setup lifecycle management
  useEffect(() => {
    if (!webapp) {
      // Development mode fallback
      document.body.classList.add('tg-dev-mode');
      return () => {
        document.body.classList.remove('tg-dev-mode');
      };
    }

    // Add platform-specific CSS classes
    const platform = webapp.platform || 'unknown';
    const isMobile = platform === 'ios' || platform === 'android';
    const colorScheme = webapp.colorScheme || 'light';

    document.body.classList.add('tg-app');
    document.body.classList.add(`tg-platform-${platform}`);
    document.body.classList.add(isMobile ? 'tg-mobile' : 'tg-desktop');
    document.body.classList.add(`tg-theme-${colorScheme}`);

    // Configure closing confirmation
    if (enableClosingConfirmation) {
      webapp.enableClosingConfirmation();
    } else {
      webapp.disableClosingConfirmation();
    }

    // Disable vertical swipes to prevent accidental minimize
    const webappAny = webapp as any;
    if (autoDisableVerticalSwipes && typeof webappAny.disableVerticalSwipes === 'function') {
      console.log('🚫 Disabling vertical swipes');
      webappAny.disableVerticalSwipes();
    }

    // Auto-initialize if requested
    if (autoReady && !readyCalledRef.current) {
      const timer = setTimeout(() => {
        ready();

        // Auto-expand after ready
        if (autoExpand) {
          setTimeout(() => {
            expand();

            // Request fullscreen after expand
            if (autoRequestFullscreen) {
              setTimeout(() => {
                requestFullscreen();
              }, 100);
            }
          }, 100);
        }
      }, 50);

      return () => {
        clearTimeout(timer);
        document.body.classList.remove('tg-app', `tg-platform-${platform}`,
          isMobile ? 'tg-mobile' : 'tg-desktop', `tg-theme-${colorScheme}`);
      };
    }

    return () => {
      document.body.classList.remove('tg-app', `tg-platform-${platform}`,
        isMobile ? 'tg-mobile' : 'tg-desktop', `tg-theme-${colorScheme}`);
    };
  }, [webapp, autoReady, autoExpand, autoRequestFullscreen, autoDisableVerticalSwipes,
      enableClosingConfirmation, ready, expand, requestFullscreen]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (webapp && enableClosingConfirmation) {
        webapp.disableClosingConfirmation();
      }
    };
  }, [webapp, enableClosingConfirmation]);

  return {
    ready,
    expand,
    requestFullscreen,
    close,
    isReady: readyCalledRef.current,
    isAvailable,
    isFullscreen: isFullscreenRef.current
  };
}

// Higher-level hook with additional utilities
export function useTelegramApp(options: TelegramLifecycleOptions = {}) {
  const lifecycle = useTelegramLifecycle(options);
  const webapp = window.Telegram?.WebApp;

  const sendData = useCallback((data: string | object) => {
    if (!webapp) return;
    const dataToSend = typeof data === 'string' ? data : JSON.stringify(data);
    (webapp as any).sendData(dataToSend);
  }, [webapp]);

  const showAlert = useCallback((message: string, callback?: () => void) => {
    if (!webapp) {
      alert(message);
      callback?.();
      return;
    }
    webapp.showAlert(message, callback);
  }, [webapp]);

  const showConfirm = useCallback((message: string, callback?: (confirmed: boolean) => void) => {
    if (!webapp) {
      const result = confirm(message);
      callback?.(result);
      return;
    }
    webapp.showConfirm(message, callback);
  }, [webapp]);

  const openLink = useCallback((url: string, tryInstantView = false) => {
    if (!webapp) {
      window.open(url, '_blank');
      return;
    }
    (webapp as any).openLink(url, { try_instant_view: tryInstantView });
  }, [webapp]);

  return {
    ...lifecycle,
    sendData,
    showAlert,
    showConfirm,
    openLink,
    platform: webapp?.platform || 'unknown',
    version: webapp?.version || 'unknown'
  };
}
